import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Instagram } from 'lucide-react';
import { useData } from '../../context/DataContext';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const { siteSettings } = useData();
  const menuRef = useRef(null);

  const navLinks = [
    { path: '/', label: 'Anasayfa' },
    { path: '/projeler', label: 'Projeler' },
    { path: '/hakkimizda', label: 'Hakkımızda' },
    { path: '/iletisim', label: 'İletişim' },
  ];

  const whatsappUrl = `https://wa.me/${siteSettings?.whatsappNumber || '905546500984'}?text=${encodeURIComponent('Merhaba Taşdemir İnşaat, proje hakkında bilgi almak istiyorum.')}`;

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isMenuOpen]);

  // Close menu on route change
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  return (
    <header ref={menuRef} className="fixed top-0 left-0 right-0 z-50 bg-anthracite/95 backdrop-blur-md border-b border-gold/20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 group">
            <img
              src="https://customer-assets.emergentagent.com/job_ca4d188f-c124-42c2-8023-1edcea1f585d/artifacts/jdaomiy2_tas%E2%95%A0%C2%BAdemir-ins%E2%95%A0%C2%BAaat-logo-02.jpg"
              alt="Taşdemir İnşaat"
              className="h-14 w-auto transition-transform duration-300 group-hover:scale-105"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`relative text-sm font-medium tracking-wide transition-colors duration-300 ${
                  isActive(link.path)
                    ? 'text-gold'
                    : 'text-gray-300 hover:text-gold'
                }`}
              >
                {link.label}
                {isActive(link.path) && (
                  <span className="absolute -bottom-1 left-0 w-full h-0.5 bg-gold" />
                )}
              </Link>
            ))}
          </nav>

          {/* Desktop Actions */}
          <div className="hidden lg:flex items-center space-x-4">
            <a
              href={siteSettings?.instagram || '#'}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 text-gray-400 hover:text-gold transition-colors duration-300"
            >
              <Instagram className="w-5 h-5" />
            </a>
            <a
              href={`tel:${siteSettings?.phone1?.replace(/\s/g, '')}`}
              className="flex items-center space-x-2 text-gray-300 hover:text-gold transition-colors duration-300"
            >
              <Phone className="w-4 h-4" />
              <span className="text-sm">{siteSettings?.phone1}</span>
            </a>
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="px-5 py-2.5 bg-gold text-anthracite font-semibold text-sm rounded hover:bg-gold-light transition-all duration-300 hover:shadow-lg hover:shadow-gold/20"
            >
              WhatsApp
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="lg:hidden p-2 text-gray-300 hover:text-gold transition-colors duration-300"
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        <div
          className={`lg:hidden overflow-hidden transition-all duration-300 ease-in-out ${
            isMenuOpen ? 'max-h-96 pb-6' : 'max-h-0'
          }`}
        >
          <nav className="flex flex-col space-y-4 pt-4">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsMenuOpen(false)}
                className={`text-base font-medium transition-colors duration-300 ${
                  isActive(link.path) ? 'text-gold' : 'text-gray-300 hover:text-gold'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <div className="flex items-center space-x-4 pt-4 border-t border-gold/20">
              <a
                href={siteSettings?.instagram || '#'}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 text-gray-400 hover:text-gold transition-colors duration-300"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 px-5 py-2.5 bg-gold text-anthracite font-semibold text-sm text-center rounded hover:bg-gold-light transition-all duration-300"
              >
                WhatsApp'tan Yaz
              </a>
            </div>
          </nav>
        </div>
      </div>
    </header>
  );
};

export default Header;
